import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Trash2 } from 'lucide-react';
import { API_URL, ADMIN_TOKEN_KEY, uploadProductImage } from './shared';
import { detectDominantColor } from '../../utils/dominantColor';
import { BadgesEditor } from './editProductParts';

export const AddProductModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1); // 1: fields, 2: images
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    material: 'algodon',
    style: 'T-Shirt',
    price_mxn: '',
    kids_price_mxn: '',
    description: '',
    sizes_adult: ['S', 'M', 'L', 'XL', 'XXL'],
    sizes_kids: [],
    has_no_tzitzit: false,
    is_new: true,
  });
  // Colors: array of { name, hex, files: File[], uploaded: [url] }
  const [colors, setColors] = useState([{ uid: 'col-0', name: 'Blanco', hex: '#FFFFFF', files: [], uploaded: [] }]);
  // Selecciones: nombre de la selección de hilos + etiquetas de opciones + selecciones personalizadas
  const [threadsCfg, setThreadsCfg] = useState({ label: 'Hilos', grueso: 'Gruesos', delgado: 'Delgados', sin: 'Sin Hilos' });
  const [customSels, setCustomSels] = useState([]);
  const [badges, setBadges] = useState([]);
  const [newSelName, setNewSelName] = useState('');
  const [newSelOpts, setNewSelOpts] = useState('');

  const addCustomSel = () => {
    const name = newSelName.trim();
    const opts = newSelOpts.split(',').map(s => s.trim()).filter(Boolean);
    if (!name || opts.length === 0) { alert('Escribe el nombre de la selección y sus opciones separadas por coma'); return; }
    setCustomSels(prev => [...prev, {
      id: `sel_${Date.now()}`, name, state: 'visible',
      options: opts.map((label, i) => ({ key: `opt_${i}_${Date.now()}`, label, state: 'visible' })),
    }]);
    setNewSelName(''); setNewSelOpts('');
  };

  const materialOptions = [
    { id: 'lana', label: 'Lana' },
    { id: 'algodon', label: 'Algodón' },
    { id: 'bambu', label: 'Bambú' },
    { id: 'poliester', label: 'Poliéster' },
    { id: 'otro', label: 'Otro' },
  ];
  const styleOptions = ['T-Shirt', 'Tank Top', 'Tank Top V-Neck', 'Oversized T-Shirt', 'Jersey', 'Kol Haposkim', 'Talit Gadol', 'Otro'];
  const adultSizeOptions = ['S', 'M', 'L', 'XL', 'XXL'];
  const kidsSizeOptions = ['2-4', '6-8', '10-12', '14-16'];

  const uploadImage = uploadProductImage;

  const handleColorFileChange = (colorIdx, files) => {
    const arr = Array.from(files);
    const target = colors[colorIdx];
    const isFirstPhoto = target && target.files.length === 0;
    setColors(prev => prev.map((c, i) => i === colorIdx ? { ...c, files: [...c.files, ...arr].slice(0, 6) } : c));
    // Auto-detect dominant color from the first photo (unless admin already picked one manually)
    if (arr[0] && isFirstPhoto && !target.userSetHex) {
      detectDominantColor(arr[0]).then(hex => {
        if (hex) setColors(prev => prev.map((c, i) => i === colorIdx && !c.userSetHex ? { ...c, hex } : c));
      });
    }
  };

  const removeColorFile = (colorIdx, fileIdx) => {
    setColors(prev => prev.map((c, i) => i === colorIdx ? { ...c, files: c.files.filter((_, k) => k !== fileIdx) } : c));
  };

  const addColor = () => setColors(prev => [...prev, { uid: `col-${Date.now()}`, name: '', hex: '#000000', files: [], uploaded: [] }]);
  const removeColor = (idx) => setColors(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    // Basic validation
    if (!form.name.trim()) { alert('Falta el nombre del producto'); return; }
    if (!form.price_mxn || parseFloat(form.price_mxn) <= 0) { alert('Falta el precio'); return; }
    if (colors.length === 0) { alert('Añade al menos un color'); return; }
    if (colors.some(c => c.files.length === 0)) { alert('Cada color necesita al menos una foto'); return; }

    setSaving(true);
    try {
      // Upload all images per color — first photo is COVER (normalized to 4:5 white), rest are gallery
      const uploadedColors = [];
      for (const c of colors) {
        const urls = [];
        for (let i = 0; i < c.files.length; i++) {
          const url = await uploadImage(c.files[i], i === 0 ? 'cover' : 'gallery');
          urls.push(url);
        }
        uploadedColors.push({
          name: { es: c.name || 'Color', en: c.name || 'Color' },
          value: c.hex,
          image: urls[0],
          gallery: urls.slice(1),
        });
      }
      // Build sizes list
      const sizesES = [
        ...form.sizes_kids.map(s => s.startsWith('X') || /^\d/.test(s) ? `Niño ${s}` : `Niño ${s}`),
        ...form.sizes_adult.map(s => `Adulto ${s}`),
      ];
      const sizesEN = [
        ...form.sizes_kids.map(s => s.startsWith('X') || /^\d/.test(s) ? `Boys ${s}` : `Boys ${s}`),
        ...form.sizes_adult.map(s => `Adult ${s}`),
      ];

      const materialLabel = materialOptions.find(m => m.id === form.material)?.label || form.material;
      const fabricMap = {
        lana: { es: '100% Lana', en: '100% Wool' },
        algodon: { es: '100% Algodón', en: '100% Cotton' },
        bambu: { es: '95% Viscosa de Bambú 5% Spandex', en: '95% Bamboo Viscose 5% Spandex' },
        poliester: { es: '95% Poliéster 5% Spandex', en: '95% Polyester 5% Spandex' },
        otro: { es: materialLabel, en: materialLabel },
      };

      const body = {
        name: { es: form.name, en: form.name },
        material: form.material,
        fabric: fabricMap[form.material] || fabricMap.otro,
        style: form.style,
        price_mxn: parseFloat(form.price_mxn),
        kids_price_mxn: form.kids_price_mxn ? parseFloat(form.kids_price_mxn) : null,
        description: { es: form.description, en: form.description },
        sizes: { es: sizesES, en: sizesEN },
        colors: uploadedColors,
        image: uploadedColors[0].image,
        no_tzitzit: form.has_no_tzitzit,
        is_new: form.is_new,
        coming_soon: false,
      };

      if (!form.has_no_tzitzit) {
        const vl = {};
        if (threadsCfg.label.trim() && threadsCfg.label.trim() !== 'Hilos') vl.threads = threadsCfg.label.trim();
        const tol = {};
        if (threadsCfg.grueso.trim() && threadsCfg.grueso.trim() !== 'Gruesos') tol.avodat_yad_grueso = threadsCfg.grueso.trim();
        if (threadsCfg.delgado.trim() && threadsCfg.delgado.trim() !== 'Delgados') tol.avodat_yad_delgado = threadsCfg.delgado.trim();
        if (threadsCfg.sin.trim() && threadsCfg.sin.trim() !== 'Sin Hilos') tol.sin_hilos = threadsCfg.sin.trim();
        body.variant_labels = vl;
        body.variant_option_labels = Object.keys(tol).length > 0 ? { threads: tol } : {};
      }
      if (customSels.length > 0) body.custom_selections = customSels;
      if (badges.length > 0) body.badges = badges.map(b => b.trim()).filter(Boolean);

      const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
      const res = await fetch(`${API_URL}/api/admin/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Create failed: ${res.status}`);
      onCreated();
    } catch (e) {
      alert(`Error al crear el producto: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleAdultSize = (s) => setForm(f => ({ ...f, sizes_adult: f.sizes_adult.includes(s) ? f.sizes_adult.filter(x => x !== s) : [...f.sizes_adult, s] }));
  const toggleKidsSize = (s) => setForm(f => ({ ...f, sizes_kids: f.sizes_kids.includes(s) ? f.sizes_kids.filter(x => x !== s) : [...f.sizes_kids, s] }));

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid="add-product-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-bold text-slate-800">➕ Añadir producto</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-add-product">✕</button>
        </div>
        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre *</label>
            <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Ej: Tzitzit Bambú Kids Edition"
              data-testid="new-product-name" />
          </div>

          {/* Material + Style */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Material *</label>
              <select value={form.material} onChange={(e) => setForm(f => ({ ...f, material: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="new-product-material">
                {materialOptions.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Estilo *</label>
              <select value={form.style} onChange={(e) => setForm(f => ({ ...f, style: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="new-product-style">
                {styleOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Precio Adulto (MXN) *</label>
              <input type="number" value={form.price_mxn} onChange={(e) => setForm(f => ({ ...f, price_mxn: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="500"
                data-testid="new-product-price" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Precio Niño (opcional)</label>
              <input type="number" value={form.kids_price_mxn} onChange={(e) => setForm(f => ({ ...f, kids_price_mxn: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="—"
                data-testid="new-product-kids-price" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              placeholder="Descripción corta del producto..."
              data-testid="new-product-desc" />
          </div>

          {/* Sizes */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tallas Adulto</label>
            <div className="flex flex-wrap gap-1.5">
              {adultSizeOptions.map(s => (
                <button key={s} type="button" onClick={() => toggleAdultSize(s)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded border ${form.sizes_adult.includes(s) ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-slate-50 text-slate-600 border-slate-300'}`}
                  data-testid={`new-product-adult-size-${s}`}>
                  {s}
                </button>
              ))}
            </div>
            <label className="block text-xs font-semibold text-slate-600 mt-3 mb-1">Tallas Niño (opcional)</label>
            <div className="flex flex-wrap gap-1.5">
              {kidsSizeOptions.map(s => (
                <button key={s} type="button" onClick={() => toggleKidsSize(s)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded border ${form.sizes_kids.includes(s) ? 'bg-amber-500 text-white border-amber-500' : 'bg-slate-50 text-slate-600 border-slate-300'}`}
                  data-testid={`new-product-kid-size-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Colors + Photos */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-600">Colores + Fotos * (max 6 por color)</label>
              <button type="button" onClick={addColor} className="text-xs text-cyan-600 font-semibold" data-testid="add-color-btn">+ Añadir color</button>
            </div>
            <p className="text-[10px] text-slate-500 mb-2 leading-tight">
              💡 La <strong>primera foto</strong> es la portada del catálogo — se recortará automáticamente a formato 4:5 para llenar la tarjeta sin barras blancas.
            </p>
            <div className="space-y-3">
              {colors.map((c, ci) => (
                <div key={c.uid || ci} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <input type="color" value={c.hex}
                      onChange={(e) => setColors(prev => prev.map((x, i) => i === ci ? { ...x, hex: e.target.value, userSetHex: true } : x))}
                      className="w-8 h-8 rounded border border-slate-300 cursor-pointer"
                      title="Se detecta solo al subir la primera foto — puedes ajustarlo"
                      data-testid={`color-hex-${ci}`} />
                    <input type="text" value={c.name}
                      onChange={(e) => setColors(prev => prev.map((x, i) => i === ci ? { ...x, name: e.target.value } : x))}
                      placeholder="Nombre del color"
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      data-testid={`color-name-${ci}`} />
                    {colors.length > 1 && (
                      <button type="button" onClick={() => removeColor(ci)} className="text-red-500 hover:text-red-700" data-testid={`remove-color-${ci}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:bg-white text-xs text-slate-600">
                    <span>📸 Sube fotos ({c.files.length}/6)</span>
                    <input type="file" multiple accept="image/*" className="hidden"
                      onChange={(e) => handleColorFileChange(ci, e.target.files)}
                      data-testid={`color-files-${ci}`} />
                  </label>
                  {c.files.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {c.files.map((f, fi) => (
                        <div key={`${f.name}-${f.size}-${f.lastModified}`} className="relative">
                          <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 object-cover rounded border border-slate-300" />
                          <button type="button" onClick={() => removeColorFile(ci, fi)}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] font-bold leading-none flex items-center justify-center">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Selecciones (hilos + personalizadas) */}
          {!form.has_no_tzitzit && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-2" data-testid="add-selections-section">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                Selección de Hilos
                <span className="ml-2 normal-case font-normal text-slate-400">nombre y opciones editables</span>
              </p>
              <input type="text" value={threadsCfg.label}
                onChange={(e) => setThreadsCfg(t => ({ ...t, label: e.target.value }))}
                placeholder="Nombre de la selección (ej: Hilos)"
                className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-bold focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="new-threads-label" />
              <div className="grid grid-cols-3 gap-1.5">
                <input type="text" value={threadsCfg.grueso}
                  onChange={(e) => setThreadsCfg(t => ({ ...t, grueso: e.target.value }))}
                  placeholder="Gruesos"
                  className="px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid="new-threads-grueso" />
                <input type="text" value={threadsCfg.delgado}
                  onChange={(e) => setThreadsCfg(t => ({ ...t, delgado: e.target.value }))}
                  placeholder="Delgados"
                  className="px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid="new-threads-delgado" />
                <input type="text" value={threadsCfg.sin}
                  onChange={(e) => setThreadsCfg(t => ({ ...t, sin: e.target.value }))}
                  placeholder="Sin Hilos"
                  className="px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid="new-threads-sin" />
              </div>

              {customSels.map((cs, ci) => (
                <div key={cs.id} className="flex items-center justify-between gap-2 bg-white border border-cyan-200 rounded px-2 py-1">
                  <span className="text-xs text-slate-700 truncate">
                    <b>{cs.name}</b>: {cs.options.map(o => o.label).join(', ')}
                  </span>
                  <button type="button" onClick={() => setCustomSels(prev => prev.filter((_, i) => i !== ci))}
                    className="text-red-500 hover:text-red-700 text-xs font-bold" data-testid={`new-custom-sel-delete-${ci}`}>×</button>
                </div>
              ))}
              <div className="border-2 border-dashed border-slate-300 rounded-md p-2 space-y-1.5">
                <p className="text-[10px] font-bold text-slate-500 uppercase">➕ Crear selección nueva (opcional)</p>
                <input type="text" value={newSelName} onChange={(e) => setNewSelName(e.target.value)}
                  placeholder='Nombre (ej: "Tipo de nudo")'
                  className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid="new-custom-sel-name" />
                <input type="text" value={newSelOpts} onChange={(e) => setNewSelOpts(e.target.value)}
                  placeholder='Opciones separadas por coma (ej: "Clásico, Doble, Reforzado")'
                  className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid="new-custom-sel-options" />
                <button type="button" onClick={addCustomSel}
                  className="px-3 py-1 bg-slate-700 text-white text-xs font-semibold rounded hover:bg-slate-800"
                  data-testid="new-add-custom-sel-btn">Añadir selección</button>
              </div>
            </div>
          )}

          {/* Badges */}
          <BadgesEditor badges={badges} setBadges={setBadges} />

          {/* Flags */}
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm(f => ({ ...f, is_new: e.target.checked }))} data-testid="new-product-is-new" />
              Badge &quot;NEW&quot;
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
              <input type="checkbox" checked={form.has_no_tzitzit} onChange={(e) => setForm(f => ({ ...f, has_no_tzitzit: e.target.checked }))} data-testid="new-product-no-tzitzit" />
              SIN tzitzit (playera sencilla)
            </label>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" data-testid="cancel-add-product">Cancelar</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-lg hover:bg-cyan-700 disabled:opacity-60"
            data-testid="submit-add-product">
            {saving ? 'Guardando…' : 'Crear producto'}
          </button>
        </div>
      </div>
    </div>
  );
};
